'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import PageviewTemplate from '#pplx/frontend/apps/ask/analytics/PageviewTemplate.js';
import { AutomationLegacyRedirect } from '#pplx/frontend/apps/ask/automations-shared/AutomationLegacyRedirect.js';
import { usePhoneSmsVerification } from '#pplx/frontend/apps/ask/hooks/phone-sms-verification-hooks/usePhoneSmsVerification.js';
import { AppLayoutProvider } from '#pplx/frontend/apps/ask/layout/primitives/AppLayoutProvider.js';
import { SearchLayout } from '#pplx/frontend/apps/ask/layout/sidebar/core/SearchLayout.js';
import { SidebarProjectCreationProvider } from '#pplx/frontend/apps/ask/layout/sidebar/SidebarProjectCreationContext.js';
import { ACADEMIC_PAGE_MODULES, ARTICLE_PAGE_MODULES, CLOUD_PICKER_PAGE_MODULES, COMET_ZEN_PAGE_MODULES, COMPUTER_ARTIFACTS_MODULES, COMPUTER_ASSET_MODULES, COMPUTER_AUTOMATIONS_PAGE_MODULES, COMPUTER_DASHBOARD_PAGE_MODULES, COMPUTER_GALLERY_MODULES, COMPUTER_TASK_PAGE_MODULES, COMPUTER_THREAD_PAGE_MODULES, DISCOVER_INDEX_PAGE_MODULES, FINANCE_PAGE_MODULES, HOME_PAGE_MODULES, LIBRARY_PAGE_MODULES, PATENTS_PAGE_MODULES, PERPLEXIFY_ME_PAGE_MODULES, SECURITY_INDEX_PAGE_MODULES, SECURITY_SOURCE_DETAIL_PAGE_MODULES, SPACES_INDEX_PAGE_MODULES, SPACES_PAGE_MODULES, THREAD_PAGE_MODULES, } from '#pplx/frontend/apps/ask/o11y/pageTTVModules.js';
import { ACCOUNT_ROUTE_HANDLERS, ACCOUNT_SETTINGS_TTV_MODULE, } from '#pplx/frontend/apps/ask/page-components/account/accountRouteMapShared.js';
import { SearchComponentsMobile } from '#pplx/frontend/apps/ask/page-components/search/search-components-mobile.js';
import { ONBOARDING_URL } from '#pplx/frontend/apps/ask/routing/onboarding.js';
import { FinanceRoutesRegexSpa, SearchRoutesRegex, } from '#pplx/frontend/apps/ask/routing/routeRegexes.js';
import { ComputerUtilitiesRoutesRegex } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { accountPathToSettingsHash, isSettingsHash, } from '#pplx/frontend/apps/ask/settings/settingsHash.js';
import { ComputerTaskPageRoute } from '#pplx/frontend/apps/ask/spa/ComputerTaskPageRoute.js';
import { GammaPageLayoutRoute } from '#pplx/frontend/apps/ask/spa/GammaPageLayoutRoute.js';
import { loadHomePage } from '#pplx/frontend/apps/ask/spa/HomePageRouteShared.js';
import { RedirectTo } from '#pplx/frontend/apps/ask/spa/redirects.js';
import { SearchComponentsRoute } from '#pplx/frontend/apps/ask/spa/SearchComponentsRoute.js';
import { SpacePageRoute } from '#pplx/frontend/apps/ask/spa/SpacePageRoute.js';
import { SwitchyardRoute } from '#pplx/frontend/apps/ask/spa/SwitchyardRoute.js';
import { CreateSpaceFromSharedThreadProvider } from '#pplx/frontend/apps/ask/spaces-redesign/CreateSpaceFromSharedThreadProvider.js';
import { useCreateSpaceModal } from '#pplx/frontend/apps/ask/spaces-shared/create-space-modal-hook/useCreateSpaceModal.js';
import StripeStatusRefresher from '#pplx/frontend/apps/ask/subscription-ui/StripeStatusRefresher.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { PagePerformanceProvider } from '#pplx/frontend/metrics/providers/PerformanceProvider.js';
import { useMarkModuleTTVOnMount } from '#pplx/frontend/metrics/providers/PerformanceProviderShared.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { matchRoute } from '#pplx/frontend/router/UrlUtils.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import React, { useMemo } from 'react';
import { Route, Switch, useRoute } from 'wouter';
const Title = Dynamic(async () => {
    const { Title } = await import('#pplx/frontend/apps/ask/spa/metadata/title.js');
    return { default: Title };
});
const PendingWorkflowDraftRouteRestorer = Dynamic(async () => {
    const { usePendingWorkflowDraftRestorer } = await import('#pplx/frontend/apps/ask/computer/workflows/usePendingWorkflowDraftRestorer.js');
    const PendingWorkflowDraftRouteRestorer = React.memo(function PendingWorkflowDraftRouteRestorer() {
        usePendingWorkflowDraftRestorer({
            restoreModal: false,
            routeToComputerOnCheckoutSuccess: true,
        });
        return null;
    });
    return { default: PendingWorkflowDraftRouteRestorer };
});
const HomePage = Dynamic(loadHomePage, { critical: true });
const ChromeNewTabPage = Dynamic(async () => {
    const { ChromeNewTabPage } = await import('#pplx/frontend/apps/ask/page-components/chrome-new-tab/ChromeNewTabPage.js');
    return { default: ChromeNewTabPage };
}, { critical: true });
const CometZenPage = Dynamic(async () => {
    const { CometZenModeWrapper } = await import('#pplx/frontend/apps/ask/runtime/CometZenMode.js');
    return { default: CometZenModeWrapper };
}, {
    critical: true,
    loading: () => (_jsx("div", { className: "flex size-full items-center justify-center opacity-10", children: _jsx(Spinner, {}) })),
});
const CloudPickerPage = Dynamic(async () => {
    return import('#pplx/frontend/apps/ask/cloud-picker/CloudPickerPage.js');
});
const LibraryPage = Dynamic(async () => {
    const { default: LibraryPageDefault } = await import('#pplx/frontend/apps/ask/page-components/library.js');
    return { default: LibraryPageDefault };
}, { critical: true });
const SpacesPageWrapper = Dynamic(async () => {
    const { default: SpacesPageWrapperDefault } = await import('#pplx/frontend/apps/ask/spa/SpacesPageWrapper.js');
    return { default: SpacesPageWrapperDefault };
}, { critical: true });
const ArticlePageLayout = Dynamic(async () => {
    const { ArticlePageLayout } = await import('#pplx/frontend/apps/ask/page-components/page/slug.js');
    return { default: ArticlePageLayout };
}, { critical: true });
const AcademicLandingPage = Dynamic(async () => {
    const { AcademicLandingPage } = await import('#pplx/frontend/apps/ask/page-components/academic.js');
    return { default: AcademicLandingPage };
}, { critical: true });
const PerplexifyMeLandingPage = Dynamic(async () => {
    const { PerplexifyMeLandingPage } = await import('#pplx/frontend/apps/ask/page-components/perplexify_me.js');
    return { default: PerplexifyMeLandingPage };
}, { critical: true });
const PatentsLandingPage = Dynamic(async () => {
    const { PatentsLandingPage } = await import('#pplx/frontend/apps/ask/page-components/patents.js');
    return { default: PatentsLandingPage };
}, { critical: true });
const HealthLandingPage = Dynamic(async () => {
    const { HealthPageWrapper } = await import('#pplx/frontend/apps/ask/health/HealthPageWrapper.js');
    return { default: HealthPageWrapper };
});
const SecurityLandingPage = Dynamic(async () => {
    const { default: SecurityIndex } = await import('#pplx/frontend/apps/ask/restricted-feature-security/index.js');
    return { default: SecurityIndex };
}, { restricted: true, critical: true });
const SecuritySourceDetailPage = Dynamic(async () => {
    const { SourceDetailPage } = await import('#pplx/frontend/apps/ask/restricted-feature-security/index.js');
    return { default: SourceDetailPage };
}, { restricted: true, critical: true });
const AutomationsPage = Dynamic(async () => {
    const { AutomationsPage } = await import('#pplx/frontend/apps/ask/automations-v2/index.js');
    return { default: AutomationsPage };
});
const ComputerDashboardPage = Dynamic(async () => {
    const { ComputerDashboardPage } = await import('#pplx/frontend/apps/ask/computer/ComputerDashboardPage.js');
    return { default: ComputerDashboardPage };
}, { critical: true });
const ComputerGalleryPage = Dynamic(async () => {
    const { ComputerGalleryPage } = await import('#pplx/frontend/apps/ask/computer/ComputerGalleryPage.js');
    return { default: ComputerGalleryPage };
}, { critical: true });
const ArtifactsPage = Dynamic(async () => {
    const { ArtifactsPage } = await import('#pplx/frontend/apps/ask/artifacts/ArtifactsPage.js');
    return { default: ArtifactsPage };
}, { critical: true });
const ComputerUtilitiesRoutes = Dynamic(async () => {
    const { ComputerUtilitiesRoutes } = await import('#pplx/frontend/apps/ask/spa/ComputerUtilitiesRoutes.js');
    return { default: ComputerUtilitiesRoutes };
}, { critical: true });
const ComputerAssetWrapperPage = Dynamic(async () => {
    const { ComputerAssetWrapperPage } = await import('#pplx/frontend/apps/ask/computer/ComputerAssetWrapper.js');
    return {
        default: () => _jsx(ComputerAssetWrapperPage, {}),
    };
}, { critical: true });
const ComputerThreadOnlyPage = Dynamic(async () => {
    const { ComputerThreadOnlyPage } = await import('#pplx/frontend/apps/ask/computer/ComputerThreadOnlyPage.js');
    return { default: ComputerThreadOnlyPage };
}, { critical: true });
const DiscoverPage = Dynamic(async () => {
    const { DiscoverPage } = await import('#pplx/frontend/apps/ask/page-components/discover.js');
    return { default: DiscoverPage };
}, { critical: true });
const FinanceRouter = Dynamic(async () => {
    const { FinanceRouter } = await import('#pplx/frontend/apps/ask/page-components/finance/sub-router.js');
    return { default: FinanceRouter };
}, { critical: true });
const CfoRouter = Dynamic(async () => {
    const { CfoRouter } = await import('#pplx/frontend/apps/ask/page-components/finance/CfoIndex.js');
    return { default: CfoRouter };
}, { critical: true });
const WizardTestbedPage = Dynamic(async () => {
    const { WizardTestbedPage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/wizard.js');
    return { default: WizardTestbedPage };
}, { restricted: true });
const WorkflowTestbedPage = Dynamic(async () => {
    const { default: WorkflowTestbedPage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/index.js');
    return { default: WorkflowTestbedPage };
}, { restricted: true });
const WorkflowAssetsPage = Dynamic(async () => {
    const { WorkflowAssetsPage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/index.js');
    return { default: WorkflowAssetsPage };
}, { restricted: true });
const WorkflowWidgetsPage = Dynamic(async () => {
    const { WorkflowWidgetsPage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/index.js');
    return { default: WorkflowWidgetsPage };
}, { restricted: true });
const WorkflowIconsPage = Dynamic(async () => {
    const { WorkflowIconsPage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/icons.js');
    return { default: WorkflowIconsPage };
}, { restricted: true });
const WorkflowAgentsPage = Dynamic(async () => {
    const { default: WorkflowAgentsPage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/agents.js');
    return { default: WorkflowAgentsPage };
}, { restricted: true });
const WorkflowTeammatePage = Dynamic(async () => {
    const { default: WorkflowTeammatePage } = await import('#pplx/frontend/apps/ask/restricted-feature-workflow-testbed/teammate.js');
    return { default: WorkflowTeammatePage };
}, { restricted: true });
// Redirect legacy Computer creation routes to /computer, preserving query params (e.g. qfill)
const RedirectToComputer = () => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return _jsx(RedirectTo, { to: `/computer${search}` });
};
const RedirectComposerSdkToHome = () => _jsx(RedirectTo, { to: "/" });
const RedirectOnboarding = () => _jsx(RedirectTo, { to: ONBOARDING_URL });
// Redirect /computer/live/:slug? to /computer/gallery/:slug?
const RedirectLiveToGallery = () => {
    const [match, params] = useRoute('/computer/live/:slug');
    const slug = match ? params?.slug : undefined;
    const to = slug ? `/computer/gallery/${slug}` : '/computer/gallery';
    return _jsx(RedirectTo, { to: to });
};
// Redirect /computer/gallery/:slug to /computer/a/:slug
const RedirectGallerySlugToAsset = () => {
    const [, params] = useRoute('/computer/gallery/:slug');
    return _jsx(RedirectTo, { to: `/computer/a/${params?.slug}` });
};
// Redirect /computer/landing/:slug? to /computer, preserving query params (e.g. qfill)
const RedirectLandingToComputer = () => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return _jsx(RedirectTo, { to: `/computer${search}` });
};
const toProjectsPath = (pathname) => pathname.replace(/^\/spaces(?=\/|$)/, '/projects');
const SpacesProjectsPathnameNormalizer = () => {
    const pathname = usePathname();
    if (pathname !== '/spaces' && !pathname.startsWith('/spaces/'))
        return null;
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    return _jsx(RedirectTo, { to: `${toProjectsPath(pathname)}${search}${hash}` });
};
/**
 * Mobile-specific route handlers that bypass SearchLayout
 * for body scroll (chrome hiding on mobile web)
 */
const MOBILE_SPA_ROUTE_HANDLERS = {
    [SearchRoutesRegex]: {
        Component: SearchComponentsMobile,
        modules: THREAD_PAGE_MODULES,
    },
};
const COMPUTER_UTILITY_ROUTE_HANDLER = {
    Component: ComputerUtilitiesRoutes,
    // Utility routes pick their TTV modules inside ComputerUtilitiesRoutes after the concrete path matches.
    modules: EMPTY_ARRAY,
};
const SPA_ROUTE_HANDLERS = {
    '/library': {
        Component: LibraryPage,
        modules: LIBRARY_PAGE_MODULES,
    },
    [SearchRoutesRegex]: {
        Component: SearchComponentsRoute,
        modules: THREAD_PAGE_MODULES,
    },
    '/b/home': {
        Component: HomePage,
        modules: HOME_PAGE_MODULES,
    },
    '/b/zen': {
        Component: CometZenPage,
        modules: COMET_ZEN_PAGE_MODULES,
    },
    '/b/cloud-picker': {
        Component: CloudPickerPage,
        modules: CLOUD_PICKER_PAGE_MODULES,
    },
    '/chrome/new-tab': {
        Component: ChromeNewTabPage,
        modules: HOME_PAGE_MODULES,
    },
    '/spaces/:slug/:threadSlug': {
        Component: SpacePageRoute,
        modules: SPACES_PAGE_MODULES,
    },
    '/projects/:slug/:threadSlug': {
        Component: SpacePageRoute,
        modules: SPACES_PAGE_MODULES,
    },
    '/spaces/:slug': {
        Component: SpacePageRoute,
        modules: SPACES_PAGE_MODULES,
    },
    '/projects/:slug': {
        Component: SpacePageRoute,
        modules: SPACES_PAGE_MODULES,
    },
    '/teammate/:uuid': {
        Component: SwitchyardRoute,
        modules: THREAD_PAGE_MODULES,
    },
    '/spaces': {
        Component: SpacesPageWrapper,
        modules: SPACES_INDEX_PAGE_MODULES,
    },
    '/projects': {
        Component: SpacesPageWrapper,
        modules: SPACES_INDEX_PAGE_MODULES,
    },
    '/page/:slug': {
        Component: ArticlePageLayout,
        modules: ARTICLE_PAGE_MODULES,
    },
    '/discover/:topic/:slug': {
        Component: ArticlePageLayout,
        modules: ARTICLE_PAGE_MODULES,
    },
    '/discover/:topic': {
        Component: DiscoverPage,
        modules: DISCOVER_INDEX_PAGE_MODULES,
    },
    '/discover': {
        Component: DiscoverPage,
        modules: DISCOVER_INDEX_PAGE_MODULES,
    },
    '/academic': {
        Component: AcademicLandingPage,
        modules: ACADEMIC_PAGE_MODULES,
    },
    '/perplexify_me': {
        Component: PerplexifyMeLandingPage,
        modules: PERPLEXIFY_ME_PAGE_MODULES,
    },
    '/patents': {
        Component: PatentsLandingPage,
        modules: PATENTS_PAGE_MODULES,
    },
    '/health': {
        Component: HealthLandingPage,
        modules: EMPTY_ARRAY,
    },
    '/onboarding-neo': {
        Component: RedirectOnboarding,
        modules: EMPTY_ARRAY,
    },
    '/security': {
        Component: SecurityLandingPage,
        modules: SECURITY_INDEX_PAGE_MODULES,
    },
    '/security/sources/:source_id': {
        Component: SecuritySourceDetailPage,
        modules: SECURITY_SOURCE_DETAIL_PAGE_MODULES,
    },
    [FinanceRoutesRegexSpa]: {
        Component: FinanceRouter,
        modules: FINANCE_PAGE_MODULES,
    },
    '/cfo/:tab?': {
        Component: CfoRouter,
        modules: FINANCE_PAGE_MODULES,
    },
    ...ACCOUNT_ROUTE_HANDLERS,
    '/composer-sdk': {
        Component: RedirectComposerSdkToHome,
        // redirect-only: intentional EMPTY_ARRAY (no TTV)
        modules: EMPTY_ARRAY,
    },
    '/computer/new': {
        Component: RedirectToComputer,
        // redirect-only: intentional EMPTY_ARRAY (no TTV)
        modules: EMPTY_ARRAY,
    },
    '/computer/t/:slug': {
        Component: ComputerThreadOnlyPage,
        modules: COMPUTER_THREAD_PAGE_MODULES,
    },
    '/session/:slug': {
        Component: ComputerTaskPageRoute,
        modules: COMPUTER_TASK_PAGE_MODULES,
    },
    '/computer/tasks/:slug': {
        Component: ComputerTaskPageRoute,
        modules: COMPUTER_TASK_PAGE_MODULES,
    },
    '/computer/tasks': {
        Component: ComputerDashboardPage,
        modules: COMPUTER_DASHBOARD_PAGE_MODULES,
    },
    '/computer/automations/:automationId/runs/:runId': {
        Component: AutomationLegacyRedirect,
        modules: EMPTY_ARRAY,
    },
    '/computer/automations/:automationId': {
        Component: AutomationLegacyRedirect,
        modules: EMPTY_ARRAY,
    },
    '/computer/automations': {
        Component: AutomationsPage,
        modules: COMPUTER_AUTOMATIONS_PAGE_MODULES,
    },
    [ComputerUtilitiesRoutesRegex]: COMPUTER_UTILITY_ROUTE_HANDLER,
    '/computer/landing/:slug': {
        Component: RedirectLandingToComputer,
        // redirect-only: intentional EMPTY_ARRAY (no TTV)
        modules: EMPTY_ARRAY,
    },
    '/computer/landing': {
        Component: RedirectLandingToComputer,
        // redirect-only: intentional EMPTY_ARRAY (no TTV)
        modules: EMPTY_ARRAY,
    },
    '/computer/gallery/:slug': {
        Component: RedirectGallerySlugToAsset,
        // redirect-only: intentional EMPTY_ARRAY (no TTV)
        modules: EMPTY_ARRAY,
    },
    '/computer/gallery': {
        Component: ComputerGalleryPage,
        modules: COMPUTER_GALLERY_MODULES,
    },
    '/computer/live/:slug?': {
        Component: RedirectLiveToGallery,
        // redirect-only: intentional EMPTY_ARRAY (no TTV)
        modules: EMPTY_ARRAY,
    },
    '/computer/artifacts': {
        Component: ArtifactsPage,
        modules: COMPUTER_ARTIFACTS_MODULES,
    },
    '/computer/a/:slug': {
        Component: ComputerAssetWrapperPage,
        modules: COMPUTER_ASSET_MODULES,
    },
    '/computer': {
        Component: ComputerDashboardPage,
        modules: COMPUTER_DASHBOARD_PAGE_MODULES,
    },
    // Blank route mainly for testing/benchmarking purposes
    '/__blank__': {
        Component: () => null,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/wizard': {
        Component: WizardTestbedPage,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/workflow/teammate': {
        Component: WorkflowTeammatePage,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/workflow/agents': {
        Component: WorkflowAgentsPage,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/workflow/assets': {
        Component: WorkflowAssetsPage,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/workflow/widgets': {
        Component: WorkflowWidgetsPage,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/workflow/icons': {
        Component: WorkflowIconsPage,
        modules: EMPTY_ARRAY,
    },
    '/__internal__/workflow': {
        Component: WorkflowTestbedPage,
        modules: EMPTY_ARRAY,
    },
    '/': {
        Component: HomePage,
        modules: HOME_PAGE_MODULES,
    },
};
const FloatingComponents = Dynamic(async () => {
    const { FloatingComponents } = await import('#pplx/frontend/apps/ask/layout/shell/floating/FloatingComponents.js');
    return { default: FloatingComponents };
}, { critical: true });
const SettingsModalOpener = Dynamic(async () => {
    const { SettingsModalOpener } = await import('#pplx/frontend/apps/ask/page-components/account/SettingsModalOpener.js');
    return { default: SettingsModalOpener };
}, { critical: true });
const OrgSettingsLayout = Dynamic(async () => {
    const { OrgSettingsLayout } = await import('#pplx/frontend/apps/ask/page-components/account/layout.js');
    return { default: OrgSettingsLayout };
}, { critical: true });
const ACCOUNT_BASE_REGEXP = /^\/account/;
const FULL_WIDTH_ACCOUNT_ROUTES = new Set(['/account/org/analytics']);
const NO_LAYOUT_ROUTES_REGEX = /^\/(?:echo|b\/(?:zen|cloud-picker))$/;
const AccountPathRedirect = ({ pathname }) => {
    const router = useRouter();
    React.useEffect(() => {
        const hash = accountPathToSettingsHash(pathname);
        if (!isSettingsHash(hash))
            return;
        const search = typeof window !== 'undefined' ? window.location.search : '';
        router.replace(`/${search}${hash}`);
    }, [pathname, router]);
    return null;
};
const isFullPageAccountPath = (pathname) => pathname.startsWith('/account/org') || pathname === '/account/connectors';
const findAccountRouteModules = (pathname, accountRoutes) => {
    const directRoute = accountRoutes.find(([path]) => path === pathname);
    if (directRoute)
        return directRoute[1].modules;
    const matchedRoute = matchRoute(accountRoutes.map(([path]) => path), pathname);
    if (!matchedRoute)
        return EMPTY_ARRAY;
    return accountRoutes.find(([path]) => path === matchedRoute.route)?.[1].modules ?? EMPTY_ARRAY;
};
const AccountRoutesSwitch = ({ pathname, modules, accountRoutes }) => {
    useMarkModuleTTVOnMount(ACCOUNT_SETTINGS_TTV_MODULE, {
        skip: !isFullPageAccountPath(pathname) || !modules.includes(ACCOUNT_SETTINGS_TTV_MODULE),
    });
    if (!isFullPageAccountPath(pathname)) {
        return _jsx(AccountPathRedirect, { pathname: pathname });
    }
    return (_jsx(OrgSettingsLayout, { childrenWidth: FULL_WIDTH_ACCOUNT_ROUTES.has(pathname) ? 'none' : undefined, children: _jsx(Switch, { children: accountRoutes.map(([path, { Component }]) => (_jsx(Route, { path: path, children: _jsx(Component, {}) }, path))) }) }));
};
const COMPUTER_ROUTE_REGEX = /^\/computer(?:\/|$)/;
const COMPUTER_ASSET_ROUTE_REGEX = /^\/computer\/a\/[^/]+$/;
const COMPUTER_THREAD_ONLY_ROUTE_REGEX = /^\/computer\/t\/[^/]+$/;
const COMPUTER_UTILITIES_ROUTE_REGEXP = new RegExp(ComputerUtilitiesRoutesRegex);
export const SpaEntryPoint = React.memo(function Home({ syncRoutes = {}, }) {
    // Redirects targeted users to /phone-verification if SMS verification is required
    usePhoneSmsVerification();
    const { isMobileUserAgent } = useIsMobile();
    const { base } = useRouter();
    const pathname = usePathname();
    const { openCreateSpaceModal } = useCreateSpaceModal();
    const updateTitle = useMemo(() => pathname !== (base || '/'), [pathname, base]);
    const allRouteHandlers = useMemo(() => {
        return {
            ...SPA_ROUTE_HANDLERS,
            ...syncRoutes,
        };
    }, [syncRoutes]);
    const accountRoutes = useMemo(() => {
        return Object.entries(allRouteHandlers).filter(([path]) => path.startsWith('/account'));
    }, [allRouteHandlers]);
    const accountRouteModules = useMemo(() => findAccountRouteModules(pathname, accountRoutes), [pathname, accountRoutes]);
    const noLayoutRoutes = useMemo(() => {
        return Object.entries(allRouteHandlers).filter(([path]) => NO_LAYOUT_ROUTES_REGEX.test(path));
    }, [allRouteHandlers]);
    const computerRoutes = useMemo(() => {
        return Object.entries(allRouteHandlers).filter(([path]) => COMPUTER_ROUTE_REGEX.test(path) &&
            !COMPUTER_ASSET_ROUTE_REGEX.test(path) &&
            !COMPUTER_THREAD_ONLY_ROUTE_REGEX.test(path));
    }, [allRouteHandlers]);
    const { Component: ComputerAssetRoute, modules: computerAssetModules } = allRouteHandlers['/computer/a/:slug'];
    const { Component: ComputerThreadOnlyRoute, modules: computerThreadOnlyModules } = allRouteHandlers['/computer/t/:slug'];
    const { Component: ComputerUtilityRoutes, modules: computerUtilityModules } = COMPUTER_UTILITY_ROUTE_HANDLER;
    const otherRoutes = useMemo(() => {
        const mobileRouteSet = new Set(Object.keys(MOBILE_SPA_ROUTE_HANDLERS));
        return Object.entries(allRouteHandlers).filter(([path]) => {
            const isAccount = path.startsWith('/account');
            const isComputer = COMPUTER_ROUTE_REGEX.test(path) || path === ComputerUtilitiesRoutesRegex;
            const noLayout = NO_LAYOUT_ROUTES_REGEX.test(path);
            // On mobile, exclude routes that are in MOBILE_SPA_ROUTE_HANDLERS
            const isMobileRoute = isMobileUserAgent && mobileRouteSet.has(path);
            return !isAccount && !isComputer && !noLayout && !isMobileRoute;
        });
    }, [isMobileUserAgent, allRouteHandlers]);
    const mobileSearchRoutes = useMemo(() => {
        if (!isMobileUserAgent)
            return [];
        return Object.entries(MOBILE_SPA_ROUTE_HANDLERS);
    }, [isMobileUserAgent]);
    return (_jsxs(PageviewTemplate, { children: [_jsx(StripeStatusRefresher, {}), _jsx(PendingWorkflowDraftRouteRestorer, {}), _jsx(SettingsModalOpener, {}), _jsx(SpacesProjectsPathnameNormalizer, {}), _jsx(SidebarProjectCreationProvider, { openCreateSpaceModal: openCreateSpaceModal, children: _jsxs(Switch, { children: [_jsxs(Route, { path: ACCOUNT_BASE_REGEXP, children: [_jsx(PagePerformanceProvider, { modules: accountRouteModules, children: _jsx(AccountRoutesSwitch, { pathname: pathname, modules: accountRouteModules, accountRoutes: accountRoutes }) }), _jsx(FloatingComponents, {})] }), mobileSearchRoutes.length > 0 &&
                            mobileSearchRoutes.map(([path, { Component, modules }]) => (_jsx(Route, { path: path.startsWith('^') ? new RegExp(path) : path, children: _jsx(AppLayoutProvider, { children: _jsx(PagePerformanceProvider, { modules: modules, children: _jsx(Component, {}) }) }) }, path))), _jsx(Route, { path: NO_LAYOUT_ROUTES_REGEX, children: _jsx(Switch, { children: noLayoutRoutes.map(([path, { Component, modules }]) => (_jsx(Route, { path: path, children: _jsx(PagePerformanceProvider, { modules: modules, children: _jsx(Component, {}) }) }, path))) }) }), _jsx(Route, { path: COMPUTER_ASSET_ROUTE_REGEX, children: _jsx(PagePerformanceProvider, { modules: computerAssetModules, children: _jsx(ComputerAssetRoute, {}) }) }), _jsx(Route, { path: COMPUTER_THREAD_ONLY_ROUTE_REGEX, children: _jsx(PagePerformanceProvider, { modules: computerThreadOnlyModules, children: _jsx(CreateSpaceFromSharedThreadProvider, { children: _jsx(ComputerThreadOnlyRoute, {}) }) }) }), _jsxs(Route, { children: [_jsx(SearchLayout, { children: _jsxs(Switch, { children: [_jsx(Route, { path: COMPUTER_ROUTE_REGEX, children: _jsx(GammaPageLayoutRoute, { children: _jsxs(Switch, { children: [_jsx(Route, { path: COMPUTER_UTILITIES_ROUTE_REGEXP, children: _jsx(PagePerformanceProvider, { modules: computerUtilityModules, children: _jsx(ComputerUtilityRoutes, {}) }) }), computerRoutes.map(([path, { Component, modules }]) => (_jsx(Route, { path: path.startsWith('^') ? new RegExp(path) : path, children: _jsx(PagePerformanceProvider, { modules: modules, children: _jsx(CreateSpaceFromSharedThreadProvider, { children: _jsx(Component, {}) }) }) }, path)))] }) }) }), _jsx(Route, { children: _jsx(Switch, { children: otherRoutes.map(([path, { Component, modules }]) => (_jsx(Route, { path: path.startsWith('^') ? new RegExp(path) : path, children: _jsx(PagePerformanceProvider, { modules: modules, children: _jsx(Component, {}) }) }, path))) }) })] }) }), _jsx(FloatingComponents, {})] })] }) }), updateTitle && _jsx(Title, {})] }));
});
//# sourceMappingURL=routes.js.map